/* Isolated editor. A verified upload adapter is required before Save can succeed.
 * configure({widthMm,heightMm,insetsMm,minimumPpi,recommendedPpi,maxBytes,onSave})
 * onSave({original,printFile,manifest}, {signal}) => Promise<{id:string}>
 * The caller must invalidate its receipt on variant/size changes and on No.
 */
(() => {
  'use strict';
  if (customElements.get('ots-photo-editor')) return;
  const G = window.OTSPhotoGeometry;
  if (!G) throw new Error('Load ots-photo-geometry.js before ots-photo-editor.js.');

  class OTSPhotoEditor extends HTMLElement {
    connectedCallback() {
      if (this.dialog) return;
      this.innerHTML = `<dialog class="ots-photo-editor" aria-label="Adjust your print photo">
        <div class="ots-photo-editor__layout">
          <header><div><h2>Adjust your photo</h2><p data-size></p></div><button type="button" data-close aria-label="Close photo editor">×</button></header>
          <p class="ots-photo-editor__hint">Drag to position. Pinch or use the slider to zoom. The darkened area will not be printed.</p>
          <canvas tabindex="0" role="img" aria-label="Photo crop preview. Use arrow keys to move the photo, plus or minus to zoom."></canvas>
          <div class="ots-photo-editor__controls">
            <label>Zoom <input type="range" min="1" max="8" step="0.01" value="1" data-zoom></label>
            <p data-status role="status" aria-live="polite"></p>
            <input type="file" accept="image/jpeg,image/png,image/webp" data-replacement hidden>
            <div class="ots-photo-editor__actions"><button type="button" data-replace>Change photo</button><button type="button" data-reset>Center photo</button><button type="button" data-save>Save photo</button></div>
          </div>
        </div>
      </dialog>`;
      this.dialog = this.querySelector('dialog');
      this.canvas = this.querySelector('canvas');
      this.ctx = this.canvas.getContext('2d');
      this.zoomInput = this.querySelector('[data-zoom]');
      this.status = this.querySelector('[data-status]');
      this.saveButton = this.querySelector('[data-save]');
      this.pointers = new Map();
      this.revision = 0;
      this.querySelector('[data-close]').addEventListener('click', () => this.close());
      this.dialog.addEventListener('cancel', (event) => { event.preventDefault(); this.close(); });
      this.dialog.addEventListener('close', () => this.restoreFocus());
      this.querySelector('[data-reset]').addEventListener('click', () => { this.reset(); this.changed(); });
      this.zoomInput.addEventListener('input', () => { this.zoom = Number(this.zoomInput.value); this.changed(); });
      const replacement = this.querySelector('[data-replacement]');
      this.querySelector('[data-replace]').addEventListener('click', () => replacement.click());
      replacement.addEventListener('change', async () => {
        const file = replacement.files[0]; replacement.value = '';
        if (!file) return;
        this.toggleBusy(true); this.status.textContent = 'Opening your photo…';
        try { await this.open(file); }
        catch (error) { this.status.textContent = error.message || 'Could not open this photo. Please choose another.'; }
        finally { this.toggleBusy(false); }
      });
      this.saveButton.addEventListener('click', () => this.save());
      this.canvas.addEventListener('pointerdown', (event) => this.pointerDown(event));
      this.canvas.addEventListener('pointermove', (event) => this.pointerMove(event));
      ['pointerup', 'pointercancel', 'lostpointercapture'].forEach((name) => this.canvas.addEventListener(name, (event) => this.pointers.delete(event.pointerId)));
      this.canvas.addEventListener('keydown', (event) => this.key(event));
      this.canvas.addEventListener('wheel', (event) => {
        if (this.busy) return;
        event.preventDefault();
        this.zoom = G.clamp(this.zoom * Math.exp(-event.deltaY * 0.002), 1, 8);
        this.changed();
      }, { passive: false });
      this.resize = new ResizeObserver(() => this.draw());
      this.resize.observe(this.canvas);
    }

    disconnectedCallback() { this.close(); this.resize?.disconnect(); this.release(); }

    configure(config) {
      this.close();
      const maxBytes = Math.min(Number(config.maxBytes ?? 10 * 1024 * 1024), 10 * 1024 * 1024);
      if (!Number.isFinite(maxBytes) || maxBytes <= 0) throw new Error('Invalid photo upload size limit.');
      this.config = { minimumPpi: 150, recommendedPpi: 300, ...config, ...G.dimensions(config), maxBytes };
      this.revision++;
      this.release();
    }

    async open(file) {
      if (!this.config) throw new Error('Photo printing is not configured.');
      this.abort?.abort();
      const revision = ++this.revision;
      if (file) {
        if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) throw new Error('Choose a JPG, PNG, or WebP image. Convert HEIC/HEIF to JPG first.');
        if (!file.size || file.size > this.config.maxBytes) throw new Error(`Choose a photo no larger than ${this.config.maxBytes / 1024 / 1024} MB. Reduce its file size or choose another photo.`);
        // Browser decoding applies EXIF orientation. Saved coordinates reference these oriented pixels.
        const bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' });
        if (revision !== this.revision) { bitmap.close(); return; }
        if (!bitmap.width || !bitmap.height || bitmap.width * bitmap.height > 40000000) {
          bitmap.close(); throw new Error('Choose an image with at most 40 megapixels.');
        }
        this.dispatchEvent(new CustomEvent('ots:photo-replacing', {bubbles:true}));
        this.release();
        this.bitmap = bitmap;
        this.original = file;
        this.reset();
      }
      if (!this.bitmap) throw new Error('Choose a photo first.');
      if (!this.dialog.open) this.focusBefore = document.activeElement;
      this.status.textContent = '';
      this.busy = false;
      this.toggleBusy(false);
      const c = this.config;
      this.querySelector('[data-size]').textContent = `${c.widthMm} × ${c.heightMm} mm`;
      this.querySelector('.ots-photo-editor__hint').textContent = Object.values(c.insetsMm).some(Boolean)
        ? 'Drag or pinch to adjust. Outside the white border is not printed. The hatched edge inside it is covered by the frame.'
        : 'Drag to position. Pinch or use the slider to zoom. The darkened area will not be printed.';
      if (!this.dialog.open) this.dialog.showModal();
      requestAnimationFrame(() => { this.draw(); this.canvas.focus(); });
    }

    release() { this.bitmap?.close(); this.bitmap = null; this.original = null; }
    reset() { this.zoom = 1; this.centerX = 0.5; this.centerY = 0.5; }
    close() {
      this.revision++;
      this.abort?.abort();
      this.busy = false;
      if (this.dialog) this.toggleBusy(false);
      this.pointers?.clear();
      this.dialog?.close();
      this.restoreFocus();
    }
    restoreFocus() { if (this.focusBefore?.isConnected) this.focusBefore.focus(); this.focusBefore = null; }
    changed() { this.status.textContent = ''; this.draw(); }
    point(event) { const b = this.canvas.getBoundingClientRect(); return { x: event.clientX - b.left, y: event.clientY - b.top }; }
    pointerDown(event) {
      if (this.busy || !this.bitmap || (event.pointerType === 'mouse' && event.button !== 0)) return;
      this.canvas.setPointerCapture(event.pointerId);
      this.pointers.set(event.pointerId, this.point(event));
    }
    pointerMove(event) {
      if (!this.pointers.has(event.pointerId) || this.busy) return;
      const before = [...this.pointers.values()];
      this.pointers.set(event.pointerId, this.point(event));
      const after = [...this.pointers.values()];
      const midpoint = (points) => ({ x: points.reduce((n, p) => n + p.x, 0) / points.length, y: points.reduce((n, p) => n + p.y, 0) / points.length });
      const a = midpoint(before), b = midpoint(after);
      const scale = this.frame.width / this.rect.width;
      const sourceX = this.rect.x + (a.x - this.frame.x) / scale;
      const sourceY = this.rect.y + (a.y - this.frame.y) / scale;
      if (after.length === 2) {
        const distance = (points) => Math.hypot(points[0].x - points[1].x, points[0].y - points[1].y);
        if (distance(before) > 1) this.zoom = G.clamp(this.zoom * distance(after) / distance(before), 1, 8);
      }
      const newRect = G.cropRect(this.bitmap.width, this.bitmap.height, this.config.widthMm / this.config.heightMm, this.zoom);
      const newScale = this.frame.width / newRect.width;
      this.centerX = (sourceX - (b.x - this.frame.x - this.frame.width / 2) / newScale) / this.bitmap.width;
      this.centerY = (sourceY - (b.y - this.frame.y - this.frame.height / 2) / newScale) / this.bitmap.height;
      this.changed();
    }
    key(event) {
      if (this.busy || !this.rect) return;
      const step = event.shiftKey ? 30 : 10;
      const dx = this.rect.width / this.frame.width * step / this.bitmap.width;
      const dy = this.rect.height / this.frame.height * step / this.bitmap.height;
      const keys = {
        ArrowLeft: () => { this.centerX += dx; }, ArrowRight: () => { this.centerX -= dx; },
        ArrowUp: () => { this.centerY += dy; }, ArrowDown: () => { this.centerY -= dy; },
        '+': () => { this.zoom = G.clamp(this.zoom * 1.1, 1, 8); }, '-': () => { this.zoom = G.clamp(this.zoom / 1.1, 1, 8); },
      };
      if (keys[event.key]) { event.preventDefault(); keys[event.key](); this.changed(); }
    }

    draw() {
      if (!this.bitmap || !this.dialog.open) return;
      const { width, height } = this.canvas.getBoundingClientRect();
      if (!width || !height) return;
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      this.canvas.width = Math.round(width * dpr); this.canvas.height = Math.round(height * dpr);
      const ctx = this.ctx;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      const ratio = this.config.widthMm / this.config.heightMm;
      const fw = Math.min(width - 48, (height - 48) * ratio), fh = fw / ratio;
      this.frame = { x: (width - fw) / 2, y: (height - fh) / 2, width: fw, height: fh };
      this.rect = G.cropRect(this.bitmap.width, this.bitmap.height, ratio, this.zoom, this.centerX, this.centerY);
      const r = this.rect, f = this.frame, scale = fw / r.width;
      this.centerX = (r.x + r.width / 2) / this.bitmap.width;
      this.centerY = (r.y + r.height / 2) / this.bitmap.height;
      ctx.fillStyle = '#20231f'; ctx.fillRect(0, 0, width, height);
      ctx.drawImage(this.bitmap, f.x - r.x * scale, f.y - r.y * scale, this.bitmap.width * scale, this.bitmap.height * scale);
      ctx.fillStyle = 'rgba(0,0,0,.65)';
      ctx.beginPath(); ctx.rect(0, 0, width, height); ctx.rect(f.x, f.y, fw, fh); ctx.fill('evenodd');
      const i = this.config.insetsMm;
      if (Object.values(i).some(Boolean)) {
        const opening = { x: f.x + fw * i.left / this.config.widthMm, y: f.y + fh * i.top / this.config.heightMm,
          width: fw * (1 - (i.left + i.right) / this.config.widthMm), height: fh * (1 - (i.top + i.bottom) / this.config.heightMm) };
        ctx.save(); ctx.beginPath(); ctx.rect(f.x, f.y, fw, fh); ctx.rect(opening.x, opening.y, opening.width, opening.height); ctx.clip('evenodd');
        ctx.fillStyle = 'rgba(0,0,0,.4)'; ctx.fillRect(f.x, f.y, fw, fh);
        ctx.strokeStyle = 'rgba(255,255,255,.5)'; ctx.lineWidth = 1;
        for (let x = f.x - fh; x < f.x + fw; x += 9) { ctx.beginPath(); ctx.moveTo(x, f.y); ctx.lineTo(x + fh, f.y + fh); ctx.stroke(); }
        ctx.restore();
      }
      ctx.strokeStyle = '#fff'; ctx.lineWidth = 2; ctx.strokeRect(f.x, f.y, fw, fh);
      this.saveButton.disabled = this.busy;
      this.zoomInput.value = this.zoom;
    }

    toggleBusy(busy) {
      this.busy = busy;
      this.querySelectorAll('[data-zoom],[data-reset],[data-replace]').forEach((input) => { input.disabled = busy; });
      this.saveButton.textContent = busy ? 'Uploading photo…' : 'Save photo';
      this.draw();
    }
    async save() {
      if (this.busy || this.saveButton.disabled) return;
      if (typeof this.config.onSave !== 'function') { this.status.textContent = 'Photo upload is not connected. Your photo has not been saved.'; return; }
      const revision = this.revision;
      const bitmap = this.bitmap, original = this.original, rect = G.pixelRect(this.rect, bitmap.width, bitmap.height), config = this.config;
      this.abort = new AbortController();
      const signal = this.abort.signal;
      this.toggleBusy(true);
      this.status.textContent = 'Preparing the full resolution print file…';
      try {
        const output = document.createElement('canvas');
        output.width = Math.max(1, Math.floor(rect.width)); output.height = Math.max(1, Math.floor(rect.height));
        const ctx = output.getContext('2d');
        ctx.fillStyle = '#fff'; ctx.fillRect(0, 0, output.width, output.height);
        ctx.drawImage(bitmap, rect.x, rect.y, rect.width, rect.height, 0, 0, output.width, output.height);
        const blob = await new Promise((resolve, reject) => output.toBlob((value) => value ? resolve(value) : reject(new Error('Could not generate the print file.')), 'image/jpeg', 0.95));
        if (revision !== this.revision) return;
        const manifest = {
          version: 1, coordinateSpace: 'exif-oriented-source-pixels', source: { width: bitmap.width, height: bitmap.height },
          crop: rect, print: G.dimensions(config), output: { width: output.width, height: output.height, type: 'image/jpeg' },
          effectivePpi: G.quality({width: output.width, height: output.height}, config, config.minimumPpi, config.recommendedPpi).ppi,
          resolutionWarning: G.quality({width: output.width, height: output.height}, config, config.minimumPpi, config.recommendedPpi).status === 'warning',
        };
        const printFile = new File([blob], 'photo-print.jpg', { type: 'image/jpeg' });
        this.status.textContent = 'Uploading photo. Please wait for confirmation…';
        const receipt = await config.onSave({ original, printFile, manifest }, { signal });
        if (revision !== this.revision || signal.aborted) return;
        if (!receipt || typeof receipt.id !== 'string' || !receipt.id) throw new Error('The upload did not return a saved photo reference. Please retry.');
        this.dispatchEvent(new CustomEvent('ots:photo-saved', { bubbles: true, detail: { receipt, manifest: receipt.manifest || manifest, previewBlob: blob } }));
        this.close();
      } catch (error) {
        if (revision === this.revision && !signal.aborted) this.status.textContent = error.message || 'Upload failed. Please retry.';
      } finally {
        if (revision === this.revision) this.toggleBusy(false);
      }
    }
  }
  customElements.define('ots-photo-editor', OTSPhotoEditor);
})();
